/**
 * Chart ⋯ menu exports (screenshots/88): PNG from the live ECharts instance, SVG from ECharts' own SVG
 * renderer (server-side mode, so it's crisp at any size), CSV from the same rows as "Show as table",
 * and a link that opens the dashboard scrolled to the widget with the same filters.
 */
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";
import { toTable, type Shape } from "./chartTypes";

echarts.use([SVGRenderer]);

export const fileSlug = (title: string) =>
  title.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "chart";

/** 2× PNG on white (transparent backgrounds print badly). */
export const toPNG = (chart: echarts.ECharts) => chart.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#FFFFFF" });

/** Re-render the current option with the SVG renderer, off-screen. `chart.getOption()` already includes the theme's merged values. */
export function toSVG(option: EChartsCoreOption, width: number, height: number, theme?: string): string {
  const c = echarts.init(null, theme ?? null, { renderer: "svg", ssr: true, width, height });
  try { c.setOption({ ...option, animation: false, backgroundColor: "#FFFFFF" }); return c.renderToSVGString(); }
  finally { c.dispose(); }
}

/** RFC 4180 CSV with a source line, so a downloaded file still says where the numbers came from. */
export function toCSV(shape: Shape, meta: { title: string; source: string; filters?: string; sample?: boolean }): string {
  const t = toTable(shape);
  const cell = (v: string | number) => { const s = String(v); return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${s.replace(/^([=+\-@])/, "'$1").replace(/"/g, '""')}"` : s; };
  const lines = [t.columns, ...t.rows].map((r) => r.map(cell).join(","));
  const header = [`# ${meta.title}`, `# Source: ${meta.source}${meta.sample ? " · SAMPLE DATA" : ""}`, ...(meta.filters ? [`# Filters: ${meta.filters}`] : [])];
  return [...header, ...lines].join("\r\n") + "\r\n";
}

/** Link to a widget on its dashboard, with the page's filters (and zoom), for "Copy link to chart". */
export function chartLink(origin: string, dashboardPath: string, widgetId: string, params: Record<string, string>): string {
  const u = new URL(dashboardPath, origin);
  for (const [k, v] of Object.entries(params)) if (v) u.searchParams.set(k, v);
  u.hash = `w-${widgetId}`;
  return u.toString();
}

/** Browser download helper. */
export function download(name: string, data: string, mime: string) {
  const href = data.startsWith("data:") ? data : URL.createObjectURL(new Blob([data], { type: mime }));
  const a = Object.assign(document.createElement("a"), { href, download: name });
  document.body.append(a); a.click(); a.remove();
  if (!data.startsWith("data:")) setTimeout(() => URL.revokeObjectURL(href), 1000);
}
